import { watchImmediate } from "@vueuse/core";
import { computed, nextTick, ref } from "vue";
import { toBoolean, toJSON, trimToNull, trimToUndefined } from "#ce/utils/props";
import { createDistanceFilterApi } from "#ce/shared/filters/distance/api/distance-filter";

export const DEFAULT_DISTANCES = Object.freeze([5, 10, 15, 20, 25]);

/**
 * Normalizes the `distances` prop into an array of mile values. Custom-element
 * attributes arrive as strings, so a JSON array string (e.g. "[5,10,25]") is
 * parsed; anything that doesn't resolve to an array falls back to the default list.
 *
 * In select mode, a seeded distance (client default or URL value) missing from
 * the list is inserted in order so the select always has an option to display
 * for it. In slider mode the list is the fixed set of snap stops — a stray seed
 * snaps to the nearest stop instead (see seededDistance).
 */
function distancesFrom(props) {
  const parsed = toJSON(trimToUndefined(props.distances), true);
  const values = Array.isArray(parsed) ? [...parsed] : [...DEFAULT_DISTANCES];

  const seeded = Number.parseInt(trimToUndefined(props.distance));
  if (!isSliderControl(props) && Number.isInteger(seeded) && !values.includes(seeded)) {
    values.push(seeded);
    values.sort((a, b) => a - b);
  }

  return values;
}

function isSliderControl(props) {
  return trimToUndefined(props.distanceControl) === "slider";
}

export const useDistanceFilter = (props) => {
  const api = props.filterApi ?? createDistanceFilterApi(props);

  const geolocationSupported = computed(() => Boolean(navigator.geolocation));

  const searchQuery = ref("");

  const currentLocationId = "current-location";

  const loading = ref(false);
  const error = ref(null);

  const lat = ref(trimToUndefined(props.lat));
  const lng = ref(trimToUndefined(props.lng));

  const hasGeoCoordinates = computed(() => lat.value && lng.value);

  const selectedLocation = ref();

  const distances = ref(distancesFrom(props));
  const distanceOptions = computed(() => distances.value.map((value) => ({ value, title: `${value} miles` })));

  // Slider control (filter_options.distance.control: slider) replaces the option
  // select with an sp-number-slider that snaps through the same distances list.
  const sliderControl = computed(() => isSliderControl(props));

  // A slider only offers the configured stops, so the seed must land on one:
  // a numeric seed (e.g. a deep-linked ?distance=60) snaps to the nearest stop,
  // while an unseeded — or non-numeric, like the select's legacy "any" sentinel —
  // distance starts at the largest: the widest search area, which is also what
  // the slider displays. Submitting the raw seed would search a radius that
  // diverges from what the control shows.
  function seededDistance() {
    const seeded = trimToNull(props.distance);

    if (!sliderControl.value) {
      return seeded;
    }

    const numeric = Number.parseInt(seeded);
    if (!distances.value.length) {
      return Number.isInteger(numeric) ? numeric : undefined;
    }

    if (Number.isInteger(numeric)) {
      return distances.value.reduce((best, stop) =>
        Math.abs(stop - numeric) < Math.abs(best - numeric) ? stop : best,
      );
    }

    // The configured list keeps its given order, so take the true maximum.
    return Math.max(...distances.value);
  }

  const selectedDistance = ref(seededDistance());

  // The distance select's prompt is its "Any Distance" (no-radius) option; a
  // falsy prompt drops that option, leaving only the concrete radii.
  const anyDistancePrompt = computed(() => (toBoolean(props.allowAny) ? "Any Distance" : undefined));

  const near = ref(trimToUndefined(props.near));

  // True while the seeded `near` or lat/lng is being resolved into a selection, so a model change
  // during that window is the mount's own value arriving rather than a pick.
  const applyingSeed = ref(false);

  const selectedDistancePrependText = ref("Within");

  const selectedDistanceText = computed(() => {
    const value = Number.parseInt(selectedDistance.value);
    const option = distanceOptions.value.find((option) => option.value === value);
    return option?.title;
  });

  const isRetrievingCurrentLocation = ref(false);

  const commonPlaces = ref([]);
  const googlePlaces = ref([]);

  /**
   * If the selected location changes, clear the geo coordinates if the location is different from the current location.
   * This is to ensure that the geo coordinates are cleared when the user selects a different location.
   *
   * Also mirror the selected location name into the search field so the user sees the chosen value in the input.
   */
  watchImmediate(selectedLocation, (location) => {
    if (location && location.value !== currentLocationId) {
      clearGeoCoordinates();
    }
    searchQuery.value = location
      ? location.value === currentLocationId
        ? props.currentLocationDisplayText
        : location.name
      : "";
  });

  /**
   * Returns the locations to display in the dropdown.
   *
   * If there are google locations, they are returned grouped by "Search Results".
   * Otherwise, the popular locations are returned grouped by "Popular Locations".
   */
  const locations = computed(() => (googlePlaces.value.length > 0 ? googlePlaces.value : commonPlaces.value));

  const model = computed(() => {
    const formData = new FormData();

    const nearValue = selectedLocation.value?.value;
    if (nearValue && nearValue !== currentLocationId) {
      formData.set("near", nearValue);
    }

    // If the selected distance is set and the near value is set, we add the distance to the form data.
    // This is to ensure that the distance is only added when the near value is set.
    if (selectedDistance.value && nearValue) {
      formData.set("distance", selectedDistance.value);
    }

    if (hasGeoCoordinates.value) {
      formData.set("lat", lat.value);
      formData.set("lng", lng.value);
    }

    return formData;
  });

  /**
   * Returns the display text for the selected location.
   * This is used to display the selected location in the activator.
   *
   * The display text is constructed as follows:
   * - If there is no selected location, return undefined.
   * - If there is a selected distance, prepend the selected distance text.
   * - Append the selected location name.
   * - Join the segments with " of ".
   *
   * @returns {string | undefined}
   *
   * @example
   * // Returns "Within 5 miles of New York"
   * selectedDistance.value = 5;
   * selectedLocation.value = { name: "New York" };
   *
   * // Returns "New York"
   * selectedDistance.value = undefined;
   * selectedLocation.value = { name: "New York" };
   */
  const displayText = computed(() => {
    if (!selectedLocation.value) {
      return undefined;
    }

    const segments = [];

    if (selectedDistanceText.value) {
      segments.push(`${selectedDistancePrependText.value} ${selectedDistanceText.value}`);
    }

    const { name, value } = selectedLocation.value;
    const text = value === currentLocationId ? props.currentLocationDisplayText : name;
    segments.push(text);

    return segments.filter(Boolean).join(" of ");
  });

  // Runs `apply` with `applyingSeed` held open through the model watcher's flush, which is where
  // the resulting emission reads it.
  async function applySeed(apply) {
    applyingSeed.value = true;
    apply();
    await nextTick();
    applyingSeed.value = false;
  }

  // Retires the seed, so a places request still in flight cannot reinstate it over what the visitor
  // did — `near` is the only thing `selectLocationIfAvailable` reads. Called from the two entry
  // points that change the selection on their behalf, rather than watched: a reset with nothing
  // selected writes `undefined` over `undefined`, which moves no ref and so fires no watcher.
  function retireSeed() {
    near.value = undefined;
  }

  async function initialize() {
    if (hasGeoCoordinates.value) {
      await applySeed(() => updateSelectedLocation(currentLocationId));
    }
    await loadCommonPlaces();
  }

  async function loadCommonPlaces() {
    loading.value = true;

    try {
      const places = await api.getCommonPlaces();
      commonPlaces.value = decoratePlaceModels("Popular Locations", ...places);
      await applySeed(selectLocationIfAvailable);
    } catch (e) {
      error.value = e;
    } finally {
      loading.value = false;
    }
  }

  function decoratePlaceModels(group, ...models) {
    const pinBackgroundColor = trimToUndefined(props.locationPinBackgroundColor);
    // A bare pin by default; when a background colour is configured, wrap the pin
    // in a circular badge of that colour (icon glyph keeps its fill token).
    const prependIcon = pinBackgroundColor
      ? { name: "map-pin-filled", background: { shape: "circle", color: pinBackgroundColor } }
      : "map-pin-filled";
    return decorateModels(
      {
        group,
        props: {
          prependIcon,
          prependIconProps: {
            fillColor: "var(--sp-comp-distance-filter-list-item-pin-fill-color, var(--sp-sys-color-primary))",
          },
        },
      },
      ...models,
    );
  }

  function decorateModels(options, ...models) {
    const { group, props = {} } = options;
    return models.map((model) => ({ ...model, group, props: { ...model.props, ...props } }));
  }

  function selectLocationIfAvailable() {
    // Nothing to seed without a `near`, or once geo coordinates supersede it. A selection made
    // while the places request was in flight is the visitor's, and the seed does not displace it —
    // that write reports as seeded, so the host would take it without re-running the search and
    // leave the results describing a location the filter no longer shows.
    if (!near.value || hasGeoCoordinates.value || selectedLocation.value) {
      return;
    }

    const location = findLocationByValue(near.value);

    if (location) {
      return updateSelectedLocation(location.value);
    }

    // If the location is not found, we assume it's a google place.
    // We update the search query and google result to display the location.
    searchQuery.value = near.value;

    const googlePlace = { value: near.value, name: near.value };
    updateGoogleResult([googlePlace]);
    selectedLocation.value = googlePlace;
  }

  function findLocationByValue(value) {
    return locations.value.find((location) => location.value === value);
  }

  /**
   * Determines if the current location should be retrieved.
   *
   * @param {String} locationValue
   * @returns {Boolean}
   */
  function shouldRetrieveCurrentLocation(locationValue) {
    return locationValue === currentLocationId && !hasGeoCoordinates.value;
  }

  function updateSelectedLocation(value) {
    if (!applyingSeed.value) {
      retireSeed();
    }

    if (shouldRetrieveCurrentLocation(value)) {
      retrieveCurrentLocation();
    } else if (value === currentLocationId) {
      selectedLocation.value = { value: currentLocationId, name: props.currentLocationDisplayText };
    } else {
      const location = findLocationByValue(value);
      selectedLocation.value = location;
    }
  }

  function selectCurrentLocation() {
    if (isRetrievingCurrentLocation.value) {
      return;
    }
    updateSelectedLocation(currentLocationId);
  }

  function unsetSelectedLocation() {
    retireSeed();
    selectedLocation.value = undefined;
  }

  /**
   * Retrieves the current location.
   * If the location retrieval is successful, the geo coordinates are updated.
   * Otherwise, the selected location is unset and an error is shown.
   */
  function retrieveCurrentLocation() {
    isRetrievingCurrentLocation.value = true;

    const cleanup = () => {
      isRetrievingCurrentLocation.value = false;
    };

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        cleanup();
        updateGeoCoordinates(coords);
        updateSelectedLocation(currentLocationId);
      },
      () => {
        cleanup();
        unsetSelectedLocation();
        showCurrentLocationRetrievalError();
      },
    );
  }

  function showCurrentLocationRetrievalError() {
    // eslint-disable-next-line no-restricted-syntax
    window.alert(props.currentLocationRetrievalErrorText);
  }

  function updateGeoCoordinates({ latitude, longitude }) {
    lat.value = latitude;
    lng.value = longitude;
  }

  function clearGeoCoordinates() {
    lat.value = undefined;
    lng.value = undefined;
  }

  /**
   * Updates the google result.
   *
   * If the result is an array, it updates the google locations. Otherwise it
   * clears the google results without touching the current selection — an
   * empty result can come from a non-OK Places status (ZERO_RESULTS, OVER_QUERY_LIMIT,
   * etc.) and must not silently clear the user's chosen location. Use
   * `handleSearchInput` for explicit user-driven clearing.
   *
   * @param {Array<Object>|undefined} result
   */
  function updateGoogleResult(result) {
    googlePlaces.value = [];

    if (Array.isArray(result) && result.length > 0) {
      googlePlaces.value = decoratePlaceModels("Search Results", ...result);
    }
  }

  /**
   * Reacts to the user typing into (or clearing) the search input. Only an
   * explicit empty input — i.e. the user clearing the field — should drop the
   * current selection; intermediate keystrokes leave the selection alone so
   * the user can refine their query without losing context.
   *
   * @param {String} query
   */
  function handleSearchInput(query) {
    if (query === "" && selectedLocation.value) {
      unsetSelectedLocation();
    }
  }

  function clear() {
    unsetSelectedLocation();
    selectedDistance.value = undefined;
    googlePlaces.value = [];

    clearGeoCoordinates();
  }
  return {
    // state
    loading,
    error,
    locations,
    model,
    selectedLocation,
    distanceOptions,
    anyDistancePrompt,
    selectedDistance,
    selectedDistanceText,
    selectedDistancePrependText,
    sliderControl,
    distances,
    displayText,
    searchQuery,
    applyingSeed,
    geolocationSupported,
    isRetrievingCurrentLocation,

    // actions
    initialize,
    findLocationByValue,
    updateGoogleResult,
    handleSearchInput,
    updateSelectedLocation,
    updateGeoCoordinates,
    selectCurrentLocation,
    clear,
  };
};
